import React, { useState } from 'react'
import { X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
const MONTHLY_PRODUCT_ID = 'prod_UZHBBbH8iQcGlW'
const ANNUAL_PRODUCT_ID = 'prod_UZHFmakf46kKIf'
const CHECKOUT_TIMEOUT_MS = 20000
const PLAN_HEADER_CLASS = 'text-center pb-2 md:pb-4 md:min-h-[194px] md:justify-center'
const FEATURE_LIST_CLASS = 'space-y-1.5 md:space-y-3 flex-grow'
const FEATURE_ITEM_CLASS = 'grid grid-cols-[1rem_1fr] md:grid-cols-[1.25rem_1fr] items-start gap-1.5 md:gap-2 text-sm md:text-base'
const FEATURE_ICON_CLASS = 'leading-5 md:leading-6'
const NEGATIVE_ICON_CLASS = 'mt-0.5 h-4 w-4 md:h-5 md:w-5 text-red-500'
const UPGRADE_MODAL_ACTION_CLASS = 'nav-get-started-lines bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:bg-none hover:bg-white hover:text-orange-600 font-bold shadow-modern hover:shadow-glow transition-all duration-300 transform hover:-translate-y-1 rounded-xl md:rounded-2xl border-0'

const getStoredSupabaseAccessToken = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  if (typeof window === 'undefined' || !supabaseUrl) return null

  try {
    const projectRef = new URL(supabaseUrl).hostname.split('.')[0]
    const authStorageValue = window.localStorage.getItem(`sb-${projectRef}-auth-token`)
    if (!authStorageValue) return null

    const session = JSON.parse(authStorageValue)
    return session?.access_token || session?.currentSession?.access_token || null
  } catch (error) {
    console.error('Unable to read Supabase session for checkout:', error)
    return null
  }
}

/**
 * Tier Upgrade Modal Component
 * Displays current tier status, benefits comparison, and upgrade option
 * Supports monthly and annual checkout via Stripe
 */
const TierUpgradeModal = ({ isOpen, onClose }) => {
  const { user, userProfile, profileLoading } = useAuth()
  const [activeCheckoutProduct, setActiveCheckoutProduct] = useState(null)
  const [checkoutError, setCheckoutError] = useState('')

  if (!isOpen) return null

  const isFree = userProfile?.tier === 'free'
  const isPremium = userProfile?.tier === 'premium'
  const currentPlanLabel = isPremium ? 'Premium' : isFree ? 'Free' : profileLoading ? 'Syncing' : 'Unavailable'

  const handleUpgradeClick = async (productId) => {
    if (!user) {
      setCheckoutError('Please log in to continue with your upgrade.')
      return
    }

    try {
      setCheckoutError('')
      setActiveCheckoutProduct(productId)

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
      const accessToken = getStoredSupabaseAccessToken()

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Checkout is not configured. Missing Supabase environment variables.')
      }

      if (!accessToken) {
        throw new Error('Your session has expired. Please log in again and retry.')
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), CHECKOUT_TIMEOUT_MS)
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ productId }),
        signal: controller.signal
      }).finally(() => clearTimeout(timeoutId))

      const data = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(data?.error || `Unable to start checkout. Server returned ${response.status}.`)
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      if (!data?.url) {
        throw new Error('Checkout URL was not returned by the server.')
      }

      window.location.href = data.url
    } catch (error) {
      const message = error?.name === 'AbortError'
        ? 'Checkout request timed out. Please try again.'
        : error?.message || 'Unable to start checkout. Please try again.'
      console.error('Checkout error:', {
        operation: 'create-checkout-session',
        productId,
        message
      })
      setCheckoutError(message)
      setActiveCheckoutProduct(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white sm:max-w-2xl sm:rounded-2xl md:max-w-7xl">
        
        {/* Header */}
        <div className="recipe-app-shell subscription-modal-header rounded-t-xl bg-recipe-gradient p-4 sm:rounded-t-2xl sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="mb-1 text-xl font-bold leading-tight text-white sm:mb-2 sm:text-3xl">
                Upgrade Your Experience
              </h2>
              <p className="text-sm text-white/90 sm:text-base">
                Current Plan: <span className="font-semibold capitalize">{currentPlanLabel}</span>
              </p>
            </div>
            <button 
              onClick={onClose}
              className="rounded-full p-1.5 text-white transition-colors hover:bg-white/20 sm:p-2"
            >
              <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tier Comparison */}
        <div className="p-3 sm:p-6">
          <div className="grid gap-3 md:grid-cols-3 md:gap-6">
            
            {/* Free Tier */}
            <Card className="flex flex-col border-2 border-gray-200">
              <CardHeader className={`${PLAN_HEADER_CLASS} p-4 sm:p-6`}>
                <CardTitle className="text-lg sm:text-2xl">Free Tier</CardTitle>
                <CardDescription className="mt-1 text-2xl font-bold text-gray-900 sm:mt-2 sm:text-3xl">
                  $0 <span className="text-xs font-normal text-gray-500 sm:text-sm">/month</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-grow flex-col p-4 pt-0 sm:p-6 sm:pt-0">
                <ul className={FEATURE_LIST_CLASS}>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="text-gray-700">Unlimited recipe searches</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="text-gray-700">Access to global recipes</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="text-gray-700">View up to 5 recipes per search</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="text-gray-700">Full recipe information</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <X className={NEGATIVE_ICON_CLASS} aria-hidden="true" strokeWidth={2} />
                    <span className="text-gray-400">Ads displayed</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <X className={NEGATIVE_ICON_CLASS} aria-hidden="true" strokeWidth={2} />
                    <span className="text-gray-400">View up to 15 recipes per search</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <X className={NEGATIVE_ICON_CLASS} aria-hidden="true" strokeWidth={2} />
                    <span className="text-gray-400">Save up to 35 favorite recipes</span>
                  </li>
                </ul>
                {isFree && (
                  <div className="mt-3 rounded-lg bg-orange-50 p-2 text-center sm:mt-6 sm:p-3">
                    <p className="text-xs font-medium text-primary sm:text-sm">Your Current Plan</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Premium Monthly Tier */}
            <Card className="flex flex-col border-2 border-brand shadow-lg">
              
              <CardHeader className={`${PLAN_HEADER_CLASS} p-4 sm:p-6`}>
                <CardTitle className="text-lg text-brand sm:text-2xl">Premium Tier ⭐</CardTitle>
                <CardDescription className="mt-0.5 text-sm font-semibold text-gray-600 sm:mt-1 sm:text-lg">
                  Monthly
                </CardDescription>
                <CardDescription className="mt-1 text-2xl font-bold text-gray-900 sm:mt-2 sm:text-3xl">
                  $4.99 <span className="text-xs font-normal text-gray-500 sm:text-sm">/month</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-grow flex-col p-4 pt-0 sm:p-6 sm:pt-0">
                <ul className={FEATURE_LIST_CLASS}>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Unlimited recipe searches</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Access to global recipes</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700"> View up to 15 recipes per search</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Full recipe information </span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Save up to 35 favorite recipes</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">100% ad-free experience</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Priority support</span>
                  </li>
                </ul>
                
                {isPremium ? (
                  <div className="mt-3 rounded-lg bg-yellow-50 p-2 text-center sm:mt-6 sm:p-3">
                    <p className="text-xs font-medium text-yellow-700 sm:text-sm">✓ You're a Premium Member!</p>
                  </div>
                ) : isFree ? (
                  <Button 
                    onClick={() => handleUpgradeClick(MONTHLY_PRODUCT_ID)}
                    disabled={!!activeCheckoutProduct}
                    className={`mt-3 h-10 w-full text-sm sm:mt-6 sm:h-12 sm:text-lg ${UPGRADE_MODAL_ACTION_CLASS}`}
                  >
                    <span className="relative z-10">
                      {activeCheckoutProduct === MONTHLY_PRODUCT_ID ? 'Redirecting to Checkout...' : 'Upgrade to Premium'}
                    </span>
                  </Button>
                ) : (
                  <div className="mt-3 rounded-lg bg-gray-50 p-2 text-center sm:mt-6 sm:p-3">
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">
                      {profileLoading ? 'Checking your current plan...' : 'Plan status unavailable'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Premium Annual Tier */}
            <Card className="relative flex flex-col border-2 border-brand shadow-xl sm:border-4">
              {/* Best Value Badge with Discount - positioned in top-right with margin adjustment */}
              <div className="absolute right-2 top-2 z-10 flex flex-col items-end gap-1 sm:right-2 sm:top-3">
                <div className="rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-bold text-yellow-900 sm:px-3 sm:py-1 sm:text-xs">
                  BEST VALUE
                </div>
                <div className="mt-4 rounded bg-green-100 px-1 py-0.5 text-[10px] font-semibold text-green-700 sm:mt-7 sm:py-1 sm:text-xs">
                  Save $19.89 (33%)
                </div>
              </div>
              
              <CardHeader className={`${PLAN_HEADER_CLASS} p-4 sm:p-6`}>
                <CardTitle className="text-lg text-brand sm:text-2xl">Premium Tier ⭐</CardTitle>
                <CardDescription className="mt-0.5 text-sm font-semibold text-gray-600 sm:mt-1 sm:text-lg">
                  Annual
                </CardDescription>
                <CardDescription className="mt-1 text-2xl font-bold text-gray-900 sm:mt-2 sm:text-3xl">
                  $39.99 <span className="text-xs font-normal text-gray-500 sm:text-sm">/year</span>
                </CardDescription>
                <div className="mt-1 sm:mt-2">
                  <span className="text-xs text-gray-500 sm:text-sm">
                    Just $3.33/month • Billed annually
                  </span>
                </div>
              </CardHeader>
              <CardContent className="flex flex-grow flex-col p-4 pt-0 sm:p-6 sm:pt-0">
                <ul className={FEATURE_LIST_CLASS}>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Unlimited recipe searches</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Access to global recipes</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">View up to 15 recipes per search</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Full recipe information</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Save up to 35 favorite recipes</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">100% ad-free experience</span>
                  </li>
                  <li className={FEATURE_ITEM_CLASS}>
                    <span className={`text-green-500 ${FEATURE_ICON_CLASS}`}>✓</span>
                    <span className="font-semibold text-gray-700">Priority support</span>
                  </li>
                </ul>
                
                {isPremium ? (
                  <div className="mt-3 rounded-lg bg-yellow-50 p-2 text-center sm:mt-6 sm:p-3">
                    <p className="text-xs font-medium text-yellow-700 sm:text-sm">✓ You're a Premium Member!</p>
                  </div>
                ) : isFree ? (
                  <Button 
                    onClick={() => handleUpgradeClick(ANNUAL_PRODUCT_ID)}
                    disabled={!!activeCheckoutProduct}
                    className={`mt-3 h-10 w-full text-sm sm:mt-6 sm:h-12 sm:text-lg ${UPGRADE_MODAL_ACTION_CLASS}`}
                  >
                    <span className="relative z-10">
                      {activeCheckoutProduct === ANNUAL_PRODUCT_ID ? 'Redirecting to Checkout...' : 'Upgrade to Premium'}
                    </span>
                  </Button>
                ) : (
                  <div className="mt-3 rounded-lg bg-gray-50 p-2 text-center sm:mt-6 sm:p-3">
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">
                      {profileLoading ? 'Checking your current plan...' : 'Plan status unavailable'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          {checkoutError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-center text-xs text-red-700 sm:mt-4 sm:p-3 sm:text-sm">
              {checkoutError}
            </div>
          )}
          <p className="mt-2 text-center text-[11px] text-gray-500 sm:mt-3 sm:text-xs">
            Displayed plan prices exclude applicable taxes. Final total is calculated at checkout.
          </p>

          {/* Additional Info */}
          <div className="mt-4 rounded-lg bg-gray-50 p-3 sm:mt-8 sm:p-4">
            <h3 className="mb-1.5 text-sm font-semibold text-gray-900 sm:mb-2 sm:text-base">Why Upgrade?</h3>
            <ul className="space-y-0.5 text-xs text-gray-700 sm:space-y-1 sm:text-sm">
              <li>• 100% Ad-free browsing for a seamless cooking experience</li>
              <li>• Access to global recipes </li>
              <li>• View up to 15 recipes per search </li>
              <li>• Save up to 35 favorite recipes </li>
              {/* <li>• Advanced recipe filters and search options (coming soon)</li> */}
              <li>• Priority Support</li>
              <li>• Early access to new features</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-3 pb-3 sm:px-6 sm:pb-6">
          <Button 
            onClick={onClose}
            className={`px-5 py-2 text-sm sm:px-8 sm:py-3 sm:text-base ${UPGRADE_MODAL_ACTION_CLASS}`}
          >
            <span className="relative z-10">Maybe Later</span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default TierUpgradeModal
